import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box, IconButton, Paper, Slide, Typography,
} from '@mui/material';
import { Close, Star, StarBorder } from '@mui/icons-material';
import { useOrientation } from '../../hooks/useOrientation';
import { SHC_BOTTOM_NAV_CLEARANCE } from '../../constants/bottomNav';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { SonosSpeaker } from '../../services/sonosApi';
import { refrescarSonos } from '../../services/sonosState';
import SonosModalPlayerView from './SonosModalPlayerView';
import { HEADER_GRADIENT, SONOS_BG, SURFACE_BORDER } from './sonosUiStyles';

interface SonosSpeakerModalProps {
  open: boolean;
  speaker: SonosSpeaker | null;
  canControl?: boolean;
  isFavorite?: boolean;
  showDeviceIp?: boolean;
  onFavoriteToggle?: (speakerId: number) => void;
  onClose: () => void;
}

const SonosSpeakerModal: React.FC<SonosSpeakerModalProps> = ({
  open,
  speaker,
  canControl = false,
  isFavorite = false,
  showDeviceIp = false,
  onFavoriteToggle,
  onClose,
}) => {
  const isHorizontal = useOrientation();
  const [isClosing, setIsClosing] = useState(false);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (open) void refrescarSonos();
    if (open) setIsClosing(false);
  }, [open, speaker?.id]);

  const beginClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 280);
  }, [onClose]);

  const requestClose = useOverlayBackButton(open, beginClose, 'sonos-speaker-modal');

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!swipeStartRef.current) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - swipeStartRef.current.y);
    if (deltaX > 80 && deltaX > deltaY) requestClose();
    swipeStartRef.current = null;
  }, [requestClose]);

  if (!open || !speaker) return null;

  const header = (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 1,
      py: 0.45,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: HEADER_GRADIENT,
      minHeight: 36,
      flexShrink: 0,
    }}>
      {onFavoriteToggle ? (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onFavoriteToggle(speaker.id);
          }}
          sx={{
            p: 0.5,
            ml: 0.5,
            '&:hover': { backgroundColor: 'rgba(255, 215, 0, 0.1)' },
          }}
        >
          {isFavorite ? (
            <Star sx={{ color: '#FFD700', fontSize: 22, filter: 'drop-shadow(0px 0px 6px rgba(255, 215, 0, 0.8))' }} />
          ) : (
            <StarBorder sx={{ color: 'white', fontSize: 22 }} />
          )}
        </IconButton>
      ) : (
        <Box sx={{ width: 36 }} />
      )}
      <Typography variant="h6" sx={{
        color: 'white',
        fontWeight: 'bold',
        fontSize: '0.75rem',
        textTransform: 'capitalize',
        textAlign: 'center',
        flex: 1,
        lineHeight: 1.2,
      }}>
        {speaker.nombre}
      </Typography>
      <IconButton onClick={requestClose} sx={{ color: 'white', p: 0.5 }}>
        <Close sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const body = (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: SONOS_BG,
    }}>
      <SonosModalPlayerView
        speakerId={speaker.id}
        canControl={canControl}
        showDeviceIp={showDeviceIp}
        deviceIp={speaker.ip}
      />
    </Box>
  );

  // Horizontal: contenido embebido (contenedor fijo lo pone App.tsx / Settings)
  if (isHorizontal) {
    return (
      <Box
        data-testid="sonos-speaker-modal"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: SONOS_BG,
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Slide direction="left" in={open && !isClosing} timeout={300} mountOnEnter unmountOnExit>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{
              width: '100%',
              height: '100%',
              backgroundColor: SONOS_BG,
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 0,
              boxShadow: 'none',
              border: 'none',
            }}>
              {header}
              {body}
            </Paper>
          </Box>
        </Slide>
      </Box>
    );
  }

  // Vertical: mismo shell que DeviceModal
  return (
    <Box
      data-testid="sonos-speaker-modal"
      onTouchStart={handleTouchStart}
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1360,
        display: open ? 'flex' : 'none',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box onClick={requestClose} sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }} />
      <Slide direction="left" in={open && !isClosing} timeout={300} mountOnEnter unmountOnExit>
        <Paper
          onTouchEnd={handleTouchEnd}
          sx={{
            position: 'fixed',
            bottom: SHC_BOTTOM_NAV_CLEARANCE,
            top: '40px',
            left: '1%',
            right: '1%',
            width: '98%',
            backgroundColor: SONOS_BG,
            color: 'white',
            zIndex: 1100,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '16px 16px 0 0',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            border: '1px solid #444',
            borderTop: '2px solid #2391FF',
          }}
        >
          {header}
          {body}
        </Paper>
      </Slide>
    </Box>
  );
};

export default SonosSpeakerModal;
